/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {useState} from 'react';
import {Col, Row} from "react-bootstrap";
import {DragDropContext, Draggable, Droppable} from "react-beautiful-dnd";

import {Glyphicon} from "../GlyphIcon";
import { ObjectUtils, GuidUtils} from "../../../Utils";
import {StreamBankListEmptyRow} from "../RecordRenderers/StreamBankListEmptyRow";
import {useSortedData} from "../../../Hooks/ListHooks";

export const OrderableMutableList = (props) => {
    
    const {
        AdditionalTypeProps,
        Data,
        DataTypeName,
        Editable,
        PrimaryKeyName: pk,
        Type,
        
        AddItem=((item) => {}),
        RemoveItem=((itemOrKey) => {}),
        UpdateItem=((itemOrKey) => {}),
        UpdateItems=((items = []) => {}),
        
        EmptyItemComponent=StreamBankListEmptyRow,
        
        sortKey,
    } = props;
    
    const dropId = GuidUtils.Generate();
    const [collapsedIds, setCollapsedIds] = useState([]);

    const data = useSortedData(Data, sortKey);
    
    const getCollapsedClassName = (sectionId) => {
        return collapsedIds.includes(sectionId) ? "pop-reorderable-collapsed" : "";
    };

    const toggleCollapsedState = (sectionId) => {

        let newCollapsedIds = collapsedIds.filter((x) => { return x !== sectionId } );

        if ( newCollapsedIds.length === collapsedIds.length ) {
            setCollapsedIds(collapsedIds.concat([sectionId]));
        } else {
            setCollapsedIds(newCollapsedIds);
        }
    };

    const onDragEnd = (result) => {
        // dropped outside the list
        if (!result.destination) {
            return;
        }

        let ret = [];
        let data = Array.from(Data);
        let startIndex = result.source.index;
        let endIndex = result.destination.index;
        const [removed] = data.splice(startIndex, 1);
        data.splice(endIndex, 0, removed);

        let counter = 0;
        data.forEach((x) => {
            if(x) {
                x[sortKey] = counter++;
                ret.push(x);
            }
        });

        UpdateItems(ret);
    };

    const getItemStyle = (isDragging, draggableStyle) => {
        return ObjectUtils.Merge(
            {
            },
            draggableStyle
        );
    };

    const getListStyle = (isDraggingOver) => {
        return {
        };
    };

    if (!dropId) {
        return null;
    }
    
    const getItemKey = (x) => {
        return x[pk] || x._tempId;
    };

    const getItemTitle = (x) => {
        let pk = pk;
        return x["name"] || (x[pk] != null ? (DataTypeName + " " + x[pk]) : ("New " + DataTypeName));
    };

    const getDraggableId = (x) => {
        return (pk ?? 'none') + ":" + getItemKey(x);
    };
    
    const renderItems = (item, i) => {
        const itemKey = getItemKey(item);
        const itemTitle = getItemTitle(item);
        const draggableId = getDraggableId(item);

        return <Draggable
            type={dropId}
            key={itemKey}
            draggableId={draggableId}
            index={i}
        >
            {(provided, snapshot) =>
                <div
                    className={"pndlm-orderable-list-node" + (snapshot.isDragging ? " pndlm-orderable-list-node-dragging" : "")}
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    style={getItemStyle(
                        snapshot.isDragging,
                        provided.draggableProps.style
                    )}
                >
                    <Row key={itemKey}
                         className={"pop-row " + getCollapsedClassName(draggableId)}>
                        {
                            Editable ?
                                <Col xs={12}
                                     className="pop-remove pop-reorderable" {...provided.dragHandleProps}
                                     onClick={() => toggleCollapsedState(draggableId)}>
                                    <a className="pndlm-drag-control">
                                        <Glyphicon glyph="menu-hamburger"/>
                                    </a>
                                    <a className="pndlm-expand-control">
                                        <Glyphicon glyph="chevron-down"/>
                                    </a>
                                    <a className="pndlm-collapse-control">
                                        <Glyphicon glyph="chevron-up"/>
                                    </a>
                                    <a className="pop-reorderable-title flex-fill">{itemTitle}</a>
                                    <a className="pndlm-remove-control"
                                       onClick={() => RemoveItem(item)}>
                                        <Glyphicon glyph="remove-sign"/>
                                    </a>
                                </Col> :
                                <div {...provided.dragHandleProps} />
                        }
                        <div className="collapsable">
                            <Col xs={12} className="pop-col">
                                <Type
                                    Key={itemKey}
                                    Item={item}
                                    UpdateItem={UpdateItem}
                                    {...(AdditionalTypeProps)}
                                />
                            </Col>
                        </div>
                    </Row>
                </div>
            }
        </Draggable>
    }

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId={dropId} type={dropId}>
                {(provided, snapshot) =>
                    <div
                      ref={provided.innerRef}
                      style={getListStyle(snapshot.isDraggingOver)}
                      className={"pndlm-orderable-list-container" + (snapshot.isDraggingOver ? " pndlm-orderable-list-container-dragging-over" : "")}
                    >
                        { data && data.map(renderItems) }
                        {provided.placeholder}
                    </div>
                }
            </Droppable>
            {
                Editable ?
                    <Row>
                        <Col xs={12}>
                            <a className="add-table float-right" onClick={() => AddItem()}>
                                <Glyphicon glyph="plus-sign">Add {DataTypeName}</Glyphicon>
                            </a>
                        </Col>
                    </Row> :
                    null
            }
            {
                ((!data?.length) && (!Editable) && EmptyItemComponent) ? (
                    <Row className={rowClassName}>
                        <EmptyItemComponent
                            fieldDef={AdditionalTypeProps?.FieldDef}
                        />
                    </Row>
                ) : null
            }
        </DragDropContext>
    );
};
